/**
 * O que faz: decisão de uma sugestão da organização personalizada na revisão: mostra a coluna sugerida e o
 *            critério confirmado (sem justificativa inventada) e oferece Aceitar, Manter, Corrigir e Ajustar regra.
 * Onde usa: ModalRevisaoAgentes, quando o item é personalizado.
 * Conexões: CorrecaoOrganizacaoLead (escolha explícita) e GavetaSuave; as ações vêm prontas do modal.
 * Tabelas: nenhuma direta (aceitar/manter/corrigir usam RPCs que também registram a calibração).
 * Data: 21/10/2026.
 */
import { useId, useState } from 'react';
import { Check, ChevronDown, Loader2, PencilLine, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GavetaSuave } from '@/components/ui/gaveta-suave';
import { cn } from '@/lib/utils';
import type { ItemRevisao } from '../contratos';
import { CorrecaoOrganizacaoLead } from '../organizacao/CorrecaoOrganizacaoLead';

interface DecisaoOrganizacaoRevisaoProps {
  item: ItemRevisao;
  etapas: Array<{ id: string; nome: string }>;
  etapaAtualId?: string | null;
  ocupado: boolean;
  onAceitar: () => void;
  onManter: () => void;
  onCorrigir: (etapaId: string) => Promise<void>;
  // Abre o assistente como rascunho; a correção deste contato não muda a regra para os demais.
  onAjustarRegra?: () => void;
}

export function DecisaoOrganizacaoRevisao({ item, etapas, etapaAtualId = null, ocupado, onAceitar, onManter, onCorrigir, onAjustarRegra }: DecisaoOrganizacaoRevisaoProps) {
  const idCorrecao = useId();
  const [corrigindo, setCorrigindo] = useState(false);
  // Regra removida depois da sugestão: aceitar levaria o card por uma regra que não existe mais.
  const semRegra = !item.criterio || !item.etapaSugerida;

  return (
    <div className="space-y-2">
      <p className="text-sm text-foreground">
        Sugestão para: <span className="font-semibold">{item.etapaSugerida ?? 'coluna removida'}</span>
      </p>
      <p className="text-xs text-muted-foreground">
        {semRegra ? 'A regra desta coluna mudou ou foi removida. Mantenha o card ou corrija a coluna.' : <>Critério: {item.criterio}</>}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" disabled={ocupado || semRegra} onClick={onAceitar}>
          {ocupado ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="mr-1 h-4 w-4" aria-hidden="true" />}
          Aceitar sugestão
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={ocupado} onClick={onManter}>Manter</Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={ocupado} aria-expanded={corrigindo} aria-controls={idCorrecao}
          onClick={() => setCorrigindo((valor) => !valor)}>
          <PencilLine className="mr-1 h-4 w-4" aria-hidden="true" />Corrigir sugestão
          <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform duration-200 motion-reduce:transition-none', corrigindo && 'rotate-180')} aria-hidden="true" />
        </Button>
        {onAjustarRegra && (
          <Button size="sm" variant="ghost" className="rounded-full" disabled={ocupado} onClick={onAjustarRegra}>
            <Settings2 className="mr-1 h-4 w-4" aria-hidden="true" />Ajustar regra da coluna
          </Button>
        )}
      </div>
      <GavetaSuave aberta={corrigindo} id={idCorrecao} className="rounded-xl border border-border bg-background p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Para qual coluna este contato deve ir?</p>
        <CorrecaoOrganizacaoLead leadId={item.leadId} etapas={etapas} etapaAtualId={etapaAtualId} onConfirmar={onCorrigir} />
      </GavetaSuave>
    </div>
  );
}
